import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Eye,
  Plus,
  Trash2,
  Upload,
  Package,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Download,
  X
} from 'lucide-react';
import { ProductService } from '../../services/productService';
import { CategoryService } from '../../services/categoryService';
import { TopupTemplateService } from '../../services/topupTemplateService';
import { UserService } from '../../services/userService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';

const RESERVED_TOPUP_SERVICES = ['free fire'];
const normalizeValue = (value = '') => String(value || '').trim().toLowerCase();
const CARD_REGION_OPTIONS = ['DZ', 'FR', 'EU', 'US', 'UK', 'ES', 'DE', 'IT', 'GLOBAL'];

const inferTopupUnit = (offer = {}) => {
  const explicitUnit = String(offer.unit || '').trim();
  if (explicitUnit) {
    return explicitUnit;
  }

  const sourceText = String(offer.value || offer.name || '').trim();
  if (!sourceText) {
    return '';
  }

  const trailingUnitMatch = sourceText.match(/([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s]{1,24})$/u);
  return trailingUnitMatch ? trailingUnitMatch[1].trim() : '';
};

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotification();
  const requestedSection = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('section') === CategoryService.CATEGORY_SECTIONS.TOPUP
      ? CategoryService.CATEGORY_SECTIONS.TOPUP
      : CategoryService.CATEGORY_SECTIONS.CARDS;
  }, [location.search]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [topupTemplates, setTopupTemplates] = useState([]);
  const [existingProducts, setExistingProducts] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    game: '',
    category: '',
    categoryId: '',
    subcategoryId: '',
    subcategoryName: '',
    section: CategoryService.CATEGORY_SECTIONS.CARDS,
    description: '',
    imageUrl: '',
    coverImageUrl: '',
    detailImageUrl: '',
    unitIconUrl: '',
    status: 'active',
    topupTemplateId: '',
    topupTemplateName: '',
    topupTemplateFamily: '',
    topupFulfillmentMode: ''
  });
  
  const [offers, setOffers] = useState([]);
  const [errors, setErrors] = useState({});
  const [showCodeImport, setShowCodeImport] = useState(null);
  const [importText, setImportText] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [codesModal, setCodesModal] = useState({
    isOpen: false,
    offer: null,
    codes: [],
    loading: false
  });
  const [codesStatusFilter, setCodesStatusFilter] = useState('all');
  const [codesSearchTerm, setCodesSearchTerm] = useState('');
  const [soldToNames, setSoldToNames] = useState({});
  const [deletingCodeId, setDeletingCodeId] = useState('');

  const selectedCategory = useMemo(() => {
    return categories.find((category) => category.id === formData.categoryId) || null;
  }, [categories, formData.categoryId]);

  const isTopupService = selectedCategory
    ? CategoryService.matchesSection(selectedCategory, CategoryService.CATEGORY_SECTIONS.TOPUP)
    : formData.section === CategoryService.CATEGORY_SECTIONS.TOPUP;

  const visibleCategories = useMemo(() => {
    return categories.filter((category) => !CategoryService.isTopupSubcategory(category));
  }, [categories]);

  const visibleSubcategories = useMemo(() => {
    if (!isTopupService || !formData.categoryId) {
      return [];
    }

    return categories.filter((category) => (
      CategoryService.isTopupSubcategory(category) &&
      String(category.parentCategoryId || '').trim() === String(formData.categoryId || '').trim()
    ));
  }, [categories, formData.categoryId, isTopupService]);

  const selectedSubcategory = useMemo(() => {
    return categories.find((category) => category.id === formData.subcategoryId) || null;
  }, [categories, formData.subcategoryId]);

  const duplicateTopupServices = useMemo(() => {
    if (!isTopupService) {
      return [];
    }

    const normalizedName = normalizeValue(formData.name);
    const normalizedGame = normalizeValue(formData.game);
    const normalizedSubcategoryId = formData.subcategoryId;
    if (!normalizedName && !normalizedGame) {
      return [];
    }

    const duplicates = existingProducts.filter((existingProduct) => {
      if (existingProduct.id === id) {
        return false;
      }

      if (existingProduct.section !== CategoryService.CATEGORY_SECTIONS.TOPUP) {
        return false;
      }

      if (formData.categoryId && existingProduct.categoryId !== formData.categoryId) {
        return false;
      }

      const sameSubcategory = normalizedSubcategoryId
        ? existingProduct.subcategoryId === normalizedSubcategoryId
        : !existingProduct.subcategoryId;

      if (!sameSubcategory) {
        return false;
      }

      const existingName = normalizeValue(existingProduct.name);
      const existingGame = normalizeValue(existingProduct.game);
      return (
        (normalizedName && existingName === normalizedName) ||
        (normalizedGame && existingGame === normalizedGame)
      );
    });

    const isReservedService = RESERVED_TOPUP_SERVICES.some((reserved) => (
      reserved === normalizedName || reserved === normalizedGame
    ));

    if (!isReservedService || normalizeValue(product?.game) === 'free fire') {
      return duplicates;
    }

    return [
      ...duplicates,
      {
        id: 'reserved_free_fire',
        name: 'Free Fire',
        game: 'Free Fire',
        reserved: true
      }
    ];
  }, [existingProducts, formData.categoryId, formData.game, formData.name, formData.subcategoryId, id, isTopupService, product?.game]);

  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      const [productData, categoriesData, templatesData, productsData] = await Promise.all([
        ProductService.getProductById(id),
        CategoryService.getActiveCategories(),
        TopupTemplateService.getActiveTemplates(),
        ProductService.getAllProducts()
      ]);
      
      if (!productData) {
        addNotification('error', 'Produit non trouvé', 'Produit introuvable');
        navigate('/admin/products');
        return;
      }

      const syncedProductData = await ProductService.syncProductOfferStocks(id, productData);

      const fallbackCategory = categoriesData.find((category) => (
        category.id === syncedProductData.categoryId ||
        CategoryService.normalizeCategory(category.name) === CategoryService.normalizeCategory(syncedProductData.category)
      )) || null;
      const fallbackSubcategory = categoriesData.find((category) => (
        CategoryService.isTopupSubcategory(category) &&
        (
          category.id === syncedProductData.subcategoryId ||
          CategoryService.normalizeCategory(category.name) === CategoryService.normalizeCategory(syncedProductData.subcategoryName)
        )
      )) || null;

      setCategories(categoriesData);
      setTopupTemplates(templatesData);
      setExistingProducts(productsData);
      setProduct(syncedProductData);
      setFormData({
        name: syncedProductData.name || '',
        game: syncedProductData.game || '',
        category: syncedProductData.category || '',
        categoryId: syncedProductData.categoryId || fallbackCategory?.id || '',
        subcategoryId: syncedProductData.subcategoryId || fallbackSubcategory?.id || '',
        subcategoryName: syncedProductData.subcategoryName || fallbackSubcategory?.name || '',
        section: syncedProductData.section || fallbackCategory?.section || CategoryService.CATEGORY_SECTIONS.CARDS,
        description: syncedProductData.description || '',
        imageUrl: syncedProductData.imageUrl || '',
        coverImageUrl: syncedProductData.coverImageUrl || '',
        detailImageUrl: syncedProductData.detailImageUrl || '',
        unitIconUrl: syncedProductData.unitIconUrl || '',
        status: syncedProductData.status || 'active',
        topupTemplateId: syncedProductData.topupTemplateId || '',
        topupTemplateName: syncedProductData.topupTemplateName || '',
        topupTemplateFamily: syncedProductData.topupTemplateFamily || '',
        topupFulfillmentMode: syncedProductData.topupFulfillmentMode || ''
      });
      
      setOffers((syncedProductData.offers || []).map((offer) => ({
        ...offer,
        unit: inferTopupUnit(offer),
        region: String(offer.region || 'DZ').trim()
      })));
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      addNotification('error', 'Erreur de chargement', 'Erreur lors du chargement du produit');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  }, [addNotification, id, navigate]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCategoryChange = (event) => {
    const categoryId = event.target.value;
    const category = categories.find((item) => item.id === categoryId) || null;
    const isTopupCategory = category
      ? CategoryService.matchesSection(category, CategoryService.CATEGORY_SECTIONS.TOPUP)
      : false;

    setFormData((current) => ({
      ...current,
      categoryId,
      category: category?.name || '',
      subcategoryId: '',
      subcategoryName: '',
      section: category?.section || current.section,
      topupTemplateId: isTopupCategory ? current.topupTemplateId : '',
      topupTemplateName: isTopupCategory ? current.topupTemplateName : '',
      topupTemplateFamily: isTopupCategory ? current.topupTemplateFamily : '',
      topupFulfillmentMode: isTopupCategory ? current.topupFulfillmentMode : ''
    }));

    if (!isTopupCategory) {
      setErrors((current) => ({
        ...current,
        subcategoryId: null,
        topupTemplateId: null,
        serviceDuplicate: null
      }));
    }
  };

  const handleSubcategoryChange = (event) => {
    const subcategoryId = event.target.value;
    const subcategory = categories.find((item) => item.id === subcategoryId) || null;

    setFormData((current) => ({
      ...current,
      subcategoryId,
      subcategoryName: subcategory?.name || ''
    }));
  };

  const handleTopupTemplateChange = (event) => {
    const templateId = event.target.value;
    const template = topupTemplates.find((item) => item.id === templateId) || null;

    setFormData((current) => ({
      ...current,
      topupTemplateId: templateId,
      topupTemplateName: template?.name || '',
      topupTemplateFamily: template?.family || '',
      topupFulfillmentMode: template?.submissionMode || ''
    }));
  };

  const addOffer = () => {
    const newOffer = {
      id: Date.now().toString(),
      name: '',
      value: '',
      unit: '',
      price: '',
      region: 'DZ',
      stock: 0
    };
    setOffers(prev => [...prev, newOffer]);
  };

  const removeOffer = (offerId) => {
    const offer = offers.find(o => o.id === offerId);
    if (offer && offer.stock > 0) {
      setOfferToDelete(offer);
      setShowDeleteModal(true);
    } else {
      setOffers(prev => prev.filter(offer => offer.id !== offerId));
    }
  };

  const confirmDeleteOffer = () => {
    if (offerToDelete) {
      setOffers(prev => prev.filter(offer => offer.id !== offerToDelete.id));
      setOfferToDelete(null);
      setShowDeleteModal(false);
      addNotification('warning', 'Offre supprimée', 'Offre supprimée. Les codes associés seront également supprimés.');
    }
  };

  const updateOffer = (offerId, field, value) => {
    setOffers(prev => prev.map(offer => 
      offer.id === offerId ? { ...offer, [field]: value } : offer
    ));
  };

  const handleCodeImport = async (offerId) => {
    if (!importText.trim()) {
      addNotification('error', 'Codes manquants', 'Veuillez saisir les codes à importer');
      return;
    }

    const codes = importText
      .split('\n')
      .map(code => code.trim())
      .filter(code => code.length > 0);

    if (codes.length === 0) {
      addNotification('error', 'Codes invalides', 'Aucun code valide trouvé');
      return;
    }

    try {
      const importResult = await ProductService.importCodes(id, offerId, codes);
      const syncedProduct = await ProductService.syncProductOfferStocks(id);
      setProduct(syncedProduct);
      setOffers(syncedProduct.offers || []);

      setImportText('');
      setShowCodeImport(null);
      if (importResult.importedCount > 0) {
        addNotification(
          'success',
          'Codes importés',
          `${importResult.importedCount} code(s) importé(s)${importResult.skippedCount ? `, ${importResult.skippedCount} doublon(s) ignoré(s)` : ''}`
        );
      } else {
        addNotification('warning', 'Aucun nouveau code', 'Tous les codes saisis existent deja pour cette offre');
      }
    } catch (error) {
      console.error('Erreur lors de l\'importation:', error);
      addNotification('error', 'Erreur d\'importation', 'Erreur lors de l\'importation des codes');
    }
  };

  const refreshCodesModal = useCallback(async (offer, preserveResults = true) => {
    setSoldToNames({});
    setCodesModal({
      isOpen: true,
      offer,
      codes: preserveResults ? codesModal.codes : [],
      loading: true
    });

    try {
      const codes = await ProductService.getOfferCodes(id, offer.id);
      const soldUserIds = Array.from(
        new Set(
          codes
            .map((entry) => String(entry.soldTo || '').trim())
            .filter(Boolean)
        )
      );

      if (soldUserIds.length > 0) {
        const resolvedNames = await Promise.all(
          soldUserIds.map(async (userId) => {
            try {
              const userProfile = await UserService.getUserProfile(userId);
              const fullName = `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim();
              return [
                userId,
                fullName ||
                  String(userProfile.displayName || '').trim() ||
                  String(userProfile.email || '').trim() ||
                  userId
              ];
            } catch (error) {
              console.error('Erreur lors de la recuperation du nom du revendeur:', error);
              return [userId, userId];
            }
          })
        );

        setSoldToNames(Object.fromEntries(resolvedNames));
      }

      setCodesModal({
        isOpen: true,
        offer,
        codes,
        loading: false
      });
    } catch (error) {
      console.error('Erreur lors du chargement des codes:', error);
      throw error;
    }
  }, [codesModal.codes, id]);

  const handleOpenCodesModal = async (offer) => {
    setCodesStatusFilter('all');
    setCodesSearchTerm('');

    try {
      await refreshCodesModal(offer, false);
    } catch (error) {
      addNotification('error', 'Erreur de chargement', 'Impossible de charger les codes de cette offre');
      setCodesModal({
        isOpen: false,
        offer: null,
        codes: [],
        loading: false
      });
    }
  };

  const handleCloseCodesModal = () => {
    setCodesModal({
      isOpen: false,
      offer: null,
      codes: [],
      loading: false
    });
    setCodesStatusFilter('all');
    setCodesSearchTerm('');
    setSoldToNames({});
  };

  const handleDeleteCode = async (codeEntry) => {
    if (!codesModal.offer || !codeEntry?.id) {
      return;
    }

    if (codeEntry.status !== 'available') {
      addNotification('warning', 'Suppression bloquee', 'Seuls les codes non vendus peuvent etre supprimes.');
      return;
    }

    const confirmed = window.confirm(`Supprimer ce code de l'offre "${codesModal.offer.name}" ?`);
    if (!confirmed) {
      return;
    }

    setDeletingCodeId(codeEntry.id);

    try {
      const updatedProduct = await ProductService.deleteOfferCode(id, codesModal.offer.id, codeEntry.id);
      setProduct(updatedProduct);
      setOffers(updatedProduct?.offers || []);

      const refreshedOffer = (updatedProduct?.offers || []).find((offer) => offer.id === codesModal.offer.id) || codesModal.offer;
      await refreshCodesModal(refreshedOffer, false);

      addNotification('success', 'Code supprime', 'Le code a ete supprime avec succes.');
    } catch (error) {
      console.error('Erreur lors de la suppression du code:', error);
      addNotification('error', 'Suppression impossible', error.message || 'Impossible de supprimer ce code.');
    } finally {
      setDeletingCodeId('');
    }
  };

  const codeStats = useMemo(() => {
    const entries = codesModal.codes || [];
    const normalizedCounter = new Map();

    entries.forEach((entry) => {
      const normalizedCode = normalizeValue(entry.code);
      if (!normalizedCode) {
        return;
      }

      normalizedCounter.set(normalizedCode, (normalizedCounter.get(normalizedCode) || 0) + 1);
    });

    return {
      total: entries.length,
      available: entries.filter((entry) => entry.status === 'available').length,
      sold: entries.filter((entry) => entry.status === 'sold').length,
      reserved: entries.filter((entry) => entry.status === 'reserved').length,
      duplicates: Array.from(normalizedCounter.values()).filter((count) => count > 1).length
    };
  }, [codesModal.codes]);

  const filteredModalCodes = useMemo(() => {
    const normalizedSearch = codesSearchTerm.trim().toLowerCase();

    return (codesModal.codes || []).filter((entry) => {
      const matchesStatus = codesStatusFilter === 'all' || entry.status === codesStatusFilter;
      const matchesSearch = !normalizedSearch ||
        String(entry.code || '').toLowerCase().includes(normalizedSearch) ||
        String(soldToNames[entry.soldTo] || entry.soldTo || '').toLowerCase().includes(normalizedSearch);

      return matchesStatus && matchesSearch;
    });
  }, [codesModal.codes, codesSearchTerm, codesStatusFilter, soldToNames]);

  const formatCodeDate = (value) => {
    if (!value) {
      return 'Non defini';
    }

    const parsedDate = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return 'Non defini';
    }

    return parsedDate.toLocaleString('fr-FR');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du produit est requis';
    }

    if (!formData.game.trim()) {
      newErrors.game = 'Le jeu est requis';
    }

    if (!formData.categoryId.trim()) {
      newErrors.categoryId = 'La categorie est requise';
    }

    if (isTopupService && !formData.subcategoryId.trim()) {
      newErrors.subcategoryId = 'La sous-categorie Topup est requise';
    }

    if (isTopupService && !formData.topupTemplateId.trim()) {
      newErrors.topupTemplateId = 'Le template Topup est requis';
    }

    if (isTopupService && duplicateTopupServices.length > 0) {
      newErrors.serviceDuplicate = 'Ce service Topup existe deja ou reserve deja un service actif';
    }

    if (offers.length === 0) {
      newErrors.offers = 'Au moins une offre est requise';
    } else {
      offers.forEach((offer, index) => {
        if (!offer.name.trim()) {
          newErrors[`offer_${index}_name`] = 'Le nom de l\'offre est requis';
        }
        if (!offer.value.trim()) {
          newErrors[`offer_${index}_value`] = 'La valeur est requise';
        }
        if (!offer.price || offer.price <= 0) {
          newErrors[`offer_${index}_price`] = 'Le prix doit être supérieur à 0';
        }
        if (isTopupService && !String(offer.unit || '').trim()) {
          newErrors[`offer_${index}_unit`] = 'L unite est requise pour un service Topup';
        }
        if (!isTopupService && !String(offer.region || '').trim()) {
          newErrors[`offer_${index}_region`] = 'La region est requise';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateProductForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du produit est requis';
    }

    if (!formData.game.trim()) {
      newErrors.game = 'Le jeu est requis';
    }

    if (!formData.categoryId.trim()) {
      newErrors.categoryId = 'La categorie est requise';
    }

    if (isTopupService && !formData.subcategoryId.trim()) {
      newErrors.subcategoryId = 'La sous-categorie Topup est requise';
    }

    if (isTopupService && !formData.topupTemplateId.trim()) {
      newErrors.topupTemplateId = 'Le template Topup est requis';
    }

    if (isTopupService && duplicateTopupServices.length > 0) {
      newErrors.serviceDuplicate = 'Ce service Topup existe deja ou reserve deja un service actif';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateProductForm()) {
      addNotification('error', 'Informations invalides', 'Veuillez corriger les erreurs dans les informations du produit');
      return;
    }

    setSaving(true);
    
    try {
      // Sauvegarder uniquement les informations du produit principal
      const productData = {
        name: formData.name,
        game: formData.game,
        category: formData.category,
        categoryId: formData.categoryId,
        subcategoryId: isTopupService ? formData.subcategoryId : '',
        subcategoryName: isTopupService ? (selectedSubcategory?.name || formData.subcategoryName || '') : '',
        section: formData.section,
        imageUrl: formData.imageUrl.trim(),
        coverImageUrl: formData.coverImageUrl.trim(),
        detailImageUrl: formData.detailImageUrl.trim(),
        unitIconUrl: formData.unitIconUrl.trim(),
        description: formData.description,
        status: formData.status,
        ...(isTopupService ? {
          topupTemplateId: formData.topupTemplateId,
          topupTemplateName: formData.topupTemplateName,
          topupTemplateFamily: formData.topupTemplateFamily,
          topupFulfillmentMode: formData.topupFulfillmentMode
        } : {
          topupTemplateId: '',
          topupTemplateName: '',
          topupTemplateFamily: '',
          topupFulfillmentMode: ''
        })
      };

      await ProductService.updateProduct(id, productData);
      addNotification('success', 'Informations mises à jour', 'Informations du produit mises à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit:', error);
      addNotification('error', 'Erreur de mise à jour', 'Erreur lors de la mise à jour des informations du produit');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      addNotification('error', 'Formulaire invalide', 'Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setSaving(true);
    
    try {
      const updatedData = {
        ...formData,
        subcategoryId: isTopupService ? formData.subcategoryId : '',
        subcategoryName: isTopupService ? (selectedSubcategory?.name || formData.subcategoryName || '') : '',
        offers: offers.map(offer => ({
          id: offer.id,
          name: offer.name,
          value: offer.value,
          ...(isTopupService ? { unit: String(offer.unit || '').trim() } : {}),
          price: parseFloat(offer.price),
          region: offer.region,
          stock: offer.stock || 0
        })),
        ...(isTopupService ? {
          topupTemplateId: formData.topupTemplateId,
          topupTemplateName: formData.topupTemplateName,
          topupTemplateFamily: formData.topupTemplateFamily,
          topupFulfillmentMode: formData.topupFulfillmentMode
        } : {
          topupTemplateId: '',
          topupTemplateName: '',
          topupTemplateFamily: '',
          topupFulfillmentMode: ''
        })
      };

      await ProductService.updateProduct(id, updatedData);
      addNotification('success', 'Produit mis à jour', 'Produit mis à jour avec succès');
      navigate(isTopupService ? '/admin/topup/services' : '/admin/products');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      addNotification('error', 'Erreur de mise à jour', 'Erreur lors de la mise à jour du produit');
    } finally {
      setSaving(false);
    }
  };

  const exportCodes = async (offerId) => {
    try {
      const codes = await ProductService.getOfferCodes(id, offerId);
      
      if (codes.length === 0) {
        addNotification('warning', 'Codes indisponibles', 'Aucun code disponible pour cette offre');
        return;
      }

      const csvContent = codes.map(code => code.code).join('\n');
      const blob = new Blob([csvContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `codes_${product.name}_${offers.find(o => o.id === offerId)?.name || offerId}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      addNotification('success', 'Codes exportés', 'Codes exportés avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      addNotification('error', 'Erreur d\'export', 'Erreur lors de l\'export des codes');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Produit introuvable</h2>
          <p className="text-gray-600 mb-4">Le produit que vous cherchez n'existe pas ou a été supprimé.</p>
          <Link
            to="/admin/products"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux produits
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link
              to={requestedSection === CategoryService.CATEGORY_SECTIONS.TOPUP ? '/admin/topup/services' : '/admin/products'}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">
              {isTopupService ? 'Modifier le service Topup' : 'Modifier le produit'}
            </h1>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
        {/* Informations du produit principal */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Package className="w-5 h-5 mr-2" />
              Informations du produit principal
            </h2>
            <button
              type="button"
              onClick={handleProductSubmit}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
            >
              {saving ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Sauvegarde...
                </>
              ) : (
                'Sauvegarder le produit'
              )}
            </button>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-700">
                  <strong>Note :</strong> Cette section permet de modifier les informations generales du service sans recréer un doublon.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du produit *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: Diamants Free Fire"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jeu *
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="game"
                  value={formData.game}
                  onChange={handleInputChange}
                  list="games-list"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.game ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Free Fire"
                />
                <datalist id="games-list">
                  <option value="Free Fire" />
                  <option value="PUBG Mobile" />
                  <option value="Mobile Legends" />
                  <option value="Call of Duty Mobile" />
                  <option value="Clash of Clans" />
                  <option value="Clash Royale" />
                  <option value="Genshin Impact" />
                  <option value="Roblox" />
                  <option value="Minecraft" />
                  <option value="Fortnite" />
                  <option value="Valorant" />
                  <option value="League of Legends" />
                </datalist>
              </div>
              {errors.game && (
                <p className="mt-1 text-sm text-red-600">{errors.game}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Tapez pour rechercher ou sélectionnez dans la liste
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categorie *
              </label>
              <select
                value={formData.categoryId}
                onChange={handleCategoryChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.categoryId ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Selectionner une categorie</option>
                {visibleCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name} - {category.section === CategoryService.CATEGORY_SECTIONS.TOPUP ? 'Topup' : 'Cartes'}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Vous pouvez reclasser ce produit entre `Cartes` et `Topup` depuis cette liste sans perdre les offres deja creees.
              </p>
            </div>

            {isTopupService ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sous-categorie Topup *
                </label>
                <select
                  value={formData.subcategoryId}
                  onChange={handleSubcategoryChange}
                  disabled={!formData.categoryId}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.subcategoryId ? 'border-red-300' : 'border-gray-300'
                  } disabled:bg-gray-100 disabled:text-gray-500`}
                >
                  <option value="">
                    {formData.categoryId ? 'Selectionner une sous-categorie' : 'Choisissez d abord une categorie'}
                  </option>
                  {visibleSubcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
                  ))}
                </select>
                {errors.subcategoryId ? (
                  <p className="mt-1 text-sm text-red-600">{errors.subcategoryId}</p>
                ) : null}
                {formData.categoryId && visibleSubcategories.length === 0 ? (
                  <p className="mt-1 text-sm text-amber-600">
                    Cette categorie Topup n a pas encore de sous-categorie active.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
              URL image par defaut
              </label>
              <input
                type="url"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://exemple.com/image.jpg"
              />
              <p className="mt-1 text-xs text-gray-500">
                Utilisee en secours si l image cover ou detail n est pas renseignee.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL image cover liste
              </label>
              <input
                type="url"
                name="coverImageUrl"
                value={formData.coverImageUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={isTopupService ? 'https://exemple.com/cover-topup.jpg' : 'https://exemple.com/cover-card.jpg'}
              />
              <p className="mt-1 text-xs text-gray-500">
                {isTopupService
                  ? 'Format recommande: rectangulaire pour la carte service Topup.'
                  : 'Format recommande: image de liste pour les jeux et produits cartes.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL image detail produit
              </label>
              <input
                type="url"
                name="detailImageUrl"
                value={formData.detailImageUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={isTopupService ? 'https://exemple.com/detail-topup.jpg' : 'https://exemple.com/detail-card.jpg'}
              />
              <p className="mt-1 text-xs text-gray-500">
                {isTopupService
                  ? 'Format recommande: carre pour la fiche produit Topup.'
                  : 'Format recommande: image dediee a la fiche produit cartes.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL image unite
              </label>
              <input
                type="url"
                name="unitIconUrl"
                value={formData.unitIconUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={isTopupService ? 'https://exemple.com/icon-uc.png' : 'https://exemple.com/icon-card.png'}
              />
              <p className="mt-1 text-xs text-gray-500">
                {isTopupService
                  ? 'Optionnel. S affiche a cote de l unite sur les cartes d offres Topup.'
                  : 'Optionnel. S affiche sur les cartes d offres du catalogue a la place de l image produit reduite.'}
              </p>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Description du produit..."
            />
          </div>

          <div className="mt-6">
            <div className={`rounded-xl border p-4 ${isTopupService ? 'border-blue-200 bg-blue-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <p className="text-sm font-semibold text-gray-800">
                {isTopupService ? 'Workflow service Topup' : 'Workflow cartes'}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {isTopupService
                  ? 'Ce service Topup reste lie a son template tant qu il est dans une categorie Topup.'
                  : 'Ce produit utilise le workflow cartes avec ses offres et ses codes existants.'}
              </p>
            </div>
          </div>

          {isTopupService && (
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Topup *
                </label>
                <select
                  value={formData.topupTemplateId}
                  onChange={handleTopupTemplateChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.topupTemplateId ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Selectionner un template Topup</option>
                  {topupTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} - {TopupTemplateService.getFamilyLabel(template.family)}
                    </option>
                  ))}
                </select>
                {errors.topupTemplateId && (
                  <p className="mt-1 text-sm text-red-600">{errors.topupTemplateId}</p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  Gere les modeles dans <Link to="/admin/topup/templates" className="text-primary-600 hover:text-primary-700">Templates Topup</Link>.
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-800">Controle anti-doublon</p>
                <p className="mt-1 text-sm text-gray-500">
                  Le workflow verifie les services Topup deja existants avant sauvegarde.
                </p>
                {duplicateTopupServices.length === 0 ? (
                  <p className="mt-3 text-sm font-medium text-emerald-600">Aucun doublon detecte.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {duplicateTopupServices.map((existingProduct) => (
                      <div key={existingProduct.id} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                        <span className="font-semibold">{existingProduct.game || existingProduct.name}</span>
                        {existingProduct.reserved ? ' existe deja comme service reserve' : ' existe deja dans les services Topup'}
                      </div>
                    ))}
                  </div>
                )}
                {errors.serviceDuplicate && (
                  <p className="mt-2 text-sm text-red-600">{errors.serviceDuplicate}</p>
                )}
              </div>
            </div>
          )}

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="draft">Brouillon</option>
            </select>
          </div>
        </div>

        {/* Offres et variantes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Offres et variantes
            </h2>
            <button
              type="button"
              onClick={addOffer}
              className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une offre
            </button>
          </div>

          {errors.offers && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.offers}</p>
            </div>
          )}

          <div className="space-y-6">
            {offers.map((offer, index) => (
              <div key={offer.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-md font-medium text-gray-900">
                    Offre #{index + 1}
                    {offer.stock > 0 && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {offer.stock} codes en stock
                      </span>
                    )}
                  </h3>
                  <button
                    type="button"
                    onClick={() => removeOffer(offer.id)}
                    className="text-red-600 hover:text-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom de l'offre *
                    </label>
                    <input
                      type="text"
                      value={offer.name}
                      onChange={(e) => updateOffer(offer.id, 'name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors[`offer_${index}_name`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: 100 + 10 Diamants"
                    />
                    {errors[`offer_${index}_name`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_name`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valeur *
                    </label>
                    <input
                      type="text"
                      value={offer.value}
                      onChange={(e) => updateOffer(offer.id, 'value', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors[`offer_${index}_value`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: 110 Diamants"
                    />
                    {errors[`offer_${index}_value`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_value`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prix (DA) *
                    </label>
                    <input
                      type="number"
                      value={offer.price}
                      onChange={(e) => updateOffer(offer.id, 'price', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors[`offer_${index}_price`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="150"
                      min="0"
                      step="0.01"
                    />
                    {errors[`offer_${index}_price`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_price`]}</p>
                    )}
                  </div>

                  {isTopupService && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Unite *
                      </label>
                      <input
                        type="text"
                        value={offer.unit || ''}
                        onChange={(e) => updateOffer(offer.id, 'unit', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          errors[`offer_${index}_unit`] ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Ex: mois, UC, Coins, Robux"
                      />
                      {errors[`offer_${index}_unit`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_unit`]}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Unite affichee cote revendeur pour cette offre.
                      </p>
                    </div>
                  )}

                  {!isTopupService && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Region *
                      </label>
                      <input
                        type="text"
                        list="card-region-options"
                        value={offer.region || ''}
                        onChange={(e) => updateOffer(offer.id, 'region', e.target.value.toUpperCase())}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          errors[`offer_${index}_region`] ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Ex: FR"
                      />
                      {errors[`offer_${index}_region`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_region`]}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Exemple: `DZ`, `FR`, `EU`, `US`, `GLOBAL`
                      </p>
                    </div>
                  )}
                </div>

                {!isTopupService && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 mr-3">
                        Codes ({offer.stock || 0})
                      </span>
                      {offer.stock > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          En stock
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleOpenCodesModal(offer)}
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir les codes
                      </button>
                      {offer.stock > 0 && (
                        <button
                          type="button"
                          onClick={() => exportCodes(offer.id)}
                          className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Exporter
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowCodeImport(offer.id)}
                        className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors text-sm"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Importer
                      </button>
                    </div>
                  </div>

                  {showCodeImport === offer.id && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nouveaux codes (un par ligne)
                      </label>
                      <textarea
                        value={importText}
                        onChange={(e) => setImportText(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={`CODE1234567890\nCODE0987654321\nCODE1122334455\n...`}
                      />
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-xs text-gray-500">
                          Ces codes seront ajoutés au stock existant.
                        </p>
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setShowCodeImport(null);
                              setImportText('');
                            }}
                            className="px-3 py-1 text-gray-600 hover:text-gray-700 transition-colors text-sm"
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCodeImport(offer.id)}
                            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors text-sm"
                          >
                            Importer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        {!isTopupService && (
          <datalist id="card-region-options">
            {CARD_REGION_OPTIONS.map((region) => (
              <option key={region} value={region} />
            ))}
          </datalist>
        )}

        <div className="flex items-center justify-end space-x-4">
          <Link
            to={requestedSection === CategoryService.CATEGORY_SECTIONS.TOPUP ? '/admin/topup/services' : '/admin/products'}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </Link>
          <button
            type="button"
            onClick={handleProductSubmit}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Sauvegarde...
              </>
            ) : (
              'Sauvegarder le produit'
            )}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Sauvegarde...
              </>
            ) : (
              'Sauvegarder tout'
            )}
          </button>
        </div>
      </form>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && offerToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Supprimer l'offre
                </h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer l'offre "{offerToDelete.name}" ? 
                Cette action supprimera également tous les codes associés ({offerToDelete.stock} codes).
              </p>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setOfferToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDeleteOffer}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {codesModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Codes enregistres - {codesModal.offer?.name || 'Offre'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Visualisez les codes vendus, non vendus et detectez les doublons.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseCodesModal}
                className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-5 p-6">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{codeStats.total}</p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm text-emerald-700">Non vendus</p>
                  <p className="mt-2 text-2xl font-bold text-emerald-700">{codeStats.available}</p>
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm text-blue-700">Vendus</p>
                  <p className="mt-2 text-2xl font-bold text-blue-700">{codeStats.sold}</p>
                </div>
                <div className={`rounded-xl border p-4 ${codeStats.duplicates > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                  <p className={`text-sm ${codeStats.duplicates > 0 ? 'text-red-700' : 'text-slate-500'}`}>Doublons detectes</p>
                  <p className={`mt-2 text-2xl font-bold ${codeStats.duplicates > 0 ? 'text-red-700' : 'text-slate-900'}`}>{codeStats.duplicates}</p>
                </div>
              </div>

              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setCodesStatusFilter('all')}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition ${codesStatusFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    Tous
                  </button>
                  <button
                    type="button"
                    onClick={() => setCodesStatusFilter('available')}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition ${codesStatusFilter === 'available' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                  >
                    Non vendus
                  </button>
                  <button
                    type="button"
                    onClick={() => setCodesStatusFilter('sold')}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition ${codesStatusFilter === 'sold' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                  >
                    Vendus
                  </button>
                </div>

                <input
                  type="text"
                  value={codesSearchTerm}
                  onChange={(event) => setCodesSearchTerm(event.target.value)}
                  placeholder="Rechercher un code ou un utilisateur..."
                  className="w-full rounded-xl border border-gray-300 px-4 py-2 text-sm outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100 lg:max-w-sm"
                />
              </div>

              {codesModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <LoadingSpinner size="lg" />
                </div>
              ) : filteredModalCodes.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-14 text-center">
                  <p className="text-base font-semibold text-slate-700">Aucun code a afficher</p>
                  <p className="mt-2 text-sm text-slate-500">
                    Changez le filtre ou importez de nouveaux codes.
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-gray-200">
                  <div className="max-h-[50vh] overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Code</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Statut</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Vendu a</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filteredModalCodes.map((codeEntry) => (
                          <tr key={codeEntry.id}>
                            <td className="px-4 py-3">
                              <p className="font-mono text-sm text-slate-900">{codeEntry.code}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                codeEntry.status === 'sold'
                                  ? 'bg-blue-100 text-blue-700'
                                  : codeEntry.status === 'available'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-amber-100 text-amber-700'
                              }`}>
                                {codeEntry.status === 'sold'
                                  ? 'Vendu'
                                  : codeEntry.status === 'available'
                                    ? 'Non vendu'
                                    : 'Reserve'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {formatCodeDate(codeEntry.soldAt || codeEntry.createdAt)}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {soldToNames[codeEntry.soldTo] || codeEntry.soldTo || '-'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => handleDeleteCode(codeEntry)}
                                disabled={codeEntry.status !== 'available' || deletingCodeId === codeEntry.id}
                                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${
                                  codeEntry.status === 'available'
                                    ? 'bg-red-50 text-red-600 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60'
                                    : 'cursor-not-allowed bg-slate-100 text-slate-400'
                                }`}
                                title={codeEntry.status === 'available' ? 'Supprimer ce code' : 'Seuls les codes non vendus peuvent etre supprimes'}
                              >
                                <Trash2 className="h-4 w-4" />
                                {deletingCodeId === codeEntry.id ? 'Suppression...' : 'Supprimer'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {codeStats.duplicates > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  Des doublons de codes ont ete detectes sur cette offre. Evitez d importer deux fois la meme carte.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProduct;
